import { useCollection, useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { db } from "@/firebase";
import { formatCurrency } from "@/utils/tools.js";
import type { Transaction } from "@/utils/types.js";
import type { Account } from "@aircap/common/account";
import type { PowensAccount, PowensTransaction } from "@aircap/common/powens";
import { collection, doc, orderBy, query, updateDoc, where } from "firebase/firestore";
import { computed, type Ref } from "vue";
import { type VueFirestoreDocumentData } from "vuefire";

export const usePowensTransactions = (
  account: Ref<VueFirestoreDocumentData<Account> | undefined>,
  filters?: {
    beneficiaryIban?: Ref<string | undefined>; // Not compatible with open banking provider
    isFuture?: Ref<boolean>;
  },
) => {
  const { t: $t, locale } = useI18n();

  const transactionsSource = computed(() => {
    if (!account.value?.bankAccountId) return null;

    if (filters?.beneficiaryIban?.value)
      throw new Error("Beneficiary Iban filter is not compatible with Powens transactions");

    return query(
      collection<PowensTransaction>(
        db,
        `accounts/${account.value?.id}/powensAccounts/${account.value?.bankAccountId}/transactions`,
      ),
      where<PowensTransaction>("deleted", "==", null),
      where<PowensTransaction>("value", "!=", null),
      ...(filters?.isFuture?.value === false
        ? [where<PowensTransaction>("coming", "==", false)]
        : []),
      ...(filters?.isFuture?.value === true
        ? [where<PowensTransaction>("coming", "==", true)]
        : []),
      orderBy<PowensTransaction>("date", "desc"),
    );
  });

  const powensAccountSource = computed(() =>
    account.value?.accountType === "powens" && account.value?.bankAccountId
      ? doc<PowensAccount>(
          db,
          `accounts/${account.value?.id}/powensAccounts/${account.value?.bankAccountId}`,
        )
      : null,
  );

  const powensAccount = useDocument<PowensAccount>(powensAccountSource);

  const { data: transactionCollection, pending: loading } =
    useCollection<PowensTransaction>(transactionsSource);

  const transactions = computed<Transaction[]>(() => {
    const powensAccountData = powensAccount.value;
    const currency = powensAccountData?.currency;
    if (!currency) return [];

    return transactionCollection.value
      .map<Transaction | null>((t) => {
        if (t.value === null) return null;

        return {
          date: t.date,
          id: t.id,
          name: t.original_wording,
          amount: t.value,
          category: t.categoryId
            ? { id: t.categoryId, name: $t(`categories.${t.categoryId}`) }
            : undefined,
          trackedCategorySplits: t.trackedCategorySplits,
          suggestedTrackedCategoryId: t.suggestedTrackedCategoryId,
          rejectedSuggestedTrackedCategoryId: t.rejectedSuggestedTrackedCategoryId,
          currencyCode: currency.id,
          amountFormatted: formatCurrency({
            value: t.value,
            currencyCode: currency.id,
            sign: true,
            locale: locale.value,
          }),
          is_future: t.coming,
          is_flagged: t.is_flagged,
          dateFormatted: {
            short: new Intl.DateTimeFormat(locale.value).format(new Date(t.date)),
            long: new Intl.DateTimeFormat(locale.value, {
              dateStyle: "long",
              timeStyle: "short",
            }).format(new Date(t.date)),
          },
        };
      })
      .filter((t) => !!t);
  });

  const flagTransaction = (transactionId: string, value: boolean) => {
    if (!account.value?.id || !account.value.bankAccountId) return;
    const transactionRef = doc<PowensTransaction>(
      db,
      `accounts/${account.value.id}/powensAccounts/${account.value.bankAccountId}/transactions`,
      transactionId,
    );
    void updateDoc(transactionRef, { is_flagged: value });
  };

  return {
    transactions,
    loading,
    flagTransaction,
  };
};
